import { FirestoreError } from 'firebase/firestore';
import { AuthError } from 'firebase/auth';
import { logger } from './logger';

export interface AppError {
  code: string;
  message: string;
  originalError?: any;
}

export class ErrorHandler {
  // Map Firebase Auth error codes to user-friendly messages
  static mapAuthError(error: AuthError): AppError {
    let message = '驗證失敗';
    
    switch (error.code) {
      case 'auth/user-not-found':
        message = '找不到此帳號，請檢查電子郵件地址';
        break;
      case 'auth/wrong-password':
        message = '密碼錯誤，請重新輸入';
        break;
      case 'auth/email-already-in-use':
        message = '此電子郵件已被使用';
        break;
      case 'auth/weak-password':
        message = '密碼強度不足，至少需要6個字符';
        break;
      case 'auth/invalid-email':
        message = '電子郵件格式無效';
        break;
      case 'auth/too-many-requests':
        message = '嘗試次數過多，請稍後再試';
        break;
      case 'auth/network-request-failed':
        message = '網路連接失敗，請檢查網路連線';
        break;
      case 'auth/user-disabled':
        message = '此帳戶已被停用';
        break;
      case 'auth/requires-recent-login':
        message = '需要重新登入以執行此操作';
        break;
      case 'auth/credential-already-in-use':
        message = '此認證已被其他帳戶使用';
        break;
      case 'auth/invalid-verification-code':
        message = '驗證碼無效';
        break;
      case 'auth/invalid-verification-id':
        message = '驗證ID無效';
        break;
      default:
        message = error.message || '驗證失敗';
        break;
    }
    
    return {
      code: error.code,
      message,
      originalError: error,
    };
  }

  // Map Firestore error codes to user-friendly messages
  static mapFirestoreError(error: FirestoreError): AppError {
    let message = '資料庫操作失敗';
    
    switch (error.code) {
      case 'permission-denied':
        message = '沒有權限執行此操作';
        break;
      case 'not-found':
        message = '找不到請求的資料';
        break;
      case 'already-exists':
        message = '資料已存在';
        break;
      case 'invalid-argument':
        message = '提供的參數無效';
        break;
      case 'deadline-exceeded':
        message = '操作超時，請稍後再試';
        break;
      case 'unavailable':
        message = '服務暫時不可用，請稍後再試';
        break;
      case 'data-loss':
        message = '資料遺失或損壞';
        break;
      case 'unauthenticated':
        message = '需要登入才能執行此操作';
        break;
      case 'resource-exhausted':
        message = '資源已耗盡，請稍後再試';
        break;
      case 'failed-precondition':
        message = '不滿足執行條件';
        break;
      case 'aborted':
        message = '操作被中止';
        break;
      case 'out-of-range':
        message = '請求超出有效範圍';
        break;
      case 'unimplemented':
        message = '功能尚未實現';
        break;
      case 'internal':
        message = '內部服務器錯誤';
        break;
      case 'cancelled':
        message = '操作已取消';
        break;
      default:
        message = error.message || '資料庫操作失敗';
        break;
    }
    
    return {
      code: error.code,
      message,
      originalError: error,
    };
  }

  /**
   * 錯誤類型檢測
   */
  static categorizeError(error: unknown): {
    category: 'network' | 'api' | 'data' | 'validation' | 'auth' | 'unknown';
    isRetryable: boolean;
    severity: 'low' | 'medium' | 'high';
  } {
    const errorMessage = (error as Error)?.message?.toLowerCase() || '';
    const errorCode = (error as any)?.code?.toLowerCase() || '';

    // INFRA-06:auth 分流提前且改用精確條件 —
    // 舊版 includes('auth') 會誤傷含 "author" 的訊息、先檢查 api 再檢查
    // validation 會把 "Invalid API key" 誤判為可重試的 api 錯誤
    if (errorCode.startsWith('auth/') || errorMessage.includes('unauthorized') ||
        errorMessage.includes('permission')) {
      return { category: 'auth', isRetryable: false, severity: 'high' };
    }

    // 網路錯誤
    if (errorMessage.includes('network') || errorMessage.includes('cors') ||
        errorMessage.includes('timeout') || errorMessage.includes('connection') ||
        errorCode.includes('network') || errorMessage.includes('circuit breaker')) {
      return { category: 'network', isRetryable: true, severity: 'medium' };
    }

    // 資料驗證錯誤 - 严重，需要修复(先於 api:避免 "Invalid API key" 被判為可重試)
    if (errorMessage.includes('validation') || errorMessage.includes('invalid') ||
        errorMessage.includes('undefined field') || errorCode.startsWith('validation/')) {
      return { category: 'validation', isRetryable: false, severity: 'high' };
    }

    // API 錯誤 - 非影響系統的股票不存在錯誤
    if (errorMessage.includes('stock not found') || errorMessage.includes('unable to get stock data') ||
        errorMessage.includes('not found in current data set') || errorMessage.includes('invalid stock code')) {
      return { category: 'api', isRetryable: false, severity: 'low' };
    }

    // API 錯誤 - 一般 API 失敗
    if (errorMessage.includes('api') || errorMessage.includes('http') ||
        errorMessage.includes('failed to fetch') || errorCode.startsWith('api/')) {
      return { category: 'api', isRetryable: true, severity: 'medium' };
    }

    // 資料庫錯誤
    if (errorMessage.includes('firestore') || errorMessage.includes('database') ||
        errorCode.includes('firestore') || errorMessage.includes('transaction')) {
      return { category: 'data', isRetryable: true, severity: 'high' };
    }

    return { category: 'unknown', isRetryable: true, severity: 'medium' };
  }

  // Map application-specific errors
  static mapApplicationError(error: Error, context?: string): AppError {
    let message = '應用程式發生錯誤';
    let code = 'app/unknown-error';

    // Check if it's a known application error
    if (error.message.includes('Failed to add transaction')) {
      message = '新增交易失敗，請檢查輸入資料';
      code = 'app/transaction-add-failed';
    } else if (error.message.includes('Failed to update portfolio')) {
      message = '更新投資組合失敗';
      code = 'app/portfolio-update-failed';
    } else if (error.message.includes('Insufficient balance')) {
      message = '餘額不足';
      code = 'app/insufficient-balance';
    } else if (error.message.includes('Invalid stock symbol')) {
      message = '無效的股票代號';
      code = 'app/invalid-stock-symbol';
    } else if (error.message.includes('Invalid quantity')) {
      message = '股數必須為正整數';
      code = 'app/invalid-quantity';
    } else if (error.message.includes('Invalid price')) {
      message = '價格必須大於0';
      code = 'app/invalid-price';
    } else {
      message = error.message || '應用程式發生錯誤';
    }

    if (context) {
      message = `${context}: ${message}`;
    }

    return {
      code,
      message,
      originalError: error,
    };
  }

  // Firestore 錯誤碼(gRPC 網域碼,不含斜線;與自訂 app/...、auth/... 區分)
  private static FIRESTORE_ERROR_CODES = new Set([
    'cancelled', 'unknown', 'invalid-argument', 'deadline-exceeded',
    'not-found', 'already-exists', 'permission-denied', 'failed-precondition',
    'aborted', 'out-of-range', 'unimplemented', 'internal', 'unavailable',
    'data-loss', 'unauthenticated', 'resource-exhausted',
  ]);

  // Generic error handler
  static handleError(error: any, context?: string): AppError {
    // INFRA-04:統一經由 logger 輸出(取代裸 console.error,受層級控制)
    logger.service.error(`Error in ${context || 'application'}`, error);

    // Check error type and handle accordingly
    // INFRA-06:以精確比對取代「任何含斜線的 code」,
    // 避免自訂的 app/...、network/... 被誤送進 Firestore 映射器
    if (error.code) {
      if (error.code.startsWith('auth/')) {
        return this.mapAuthError(error as AuthError);
      } else if (this.FIRESTORE_ERROR_CODES.has(error.code)) {
        return this.mapFirestoreError(error as FirestoreError);
      }
    }

    // Handle standard Error objects
    if (error instanceof Error) {
      return this.mapApplicationError(error, context);
    }

    // Handle unknown errors
    return {
      code: 'app/unknown-error',
      message: '發生未知錯誤，請稍後再試',
      originalError: error,
    };
  }

  // Validation errors
  static createValidationError(field: string, message: string): AppError {
    return {
      code: 'validation/invalid-field',
      message: `${field}: ${message}`,
    };
  }

  // Network errors
  static createNetworkError(): AppError {
    return {
      code: 'network/request-failed',
      message: '網路連接失敗，請檢查網路連線後重試',
    };
  }
}

// React Error Boundary helper
export class ErrorBoundaryHelper {
  static logError(error: Error, errorInfo: any) {
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    // In production, you would send this to an error reporting service
    // like Sentry, LogRocket, or Firebase Crashlytics
  }

  static getErrorMessage(error: Error): string {
    const appError = ErrorHandler.handleError(error, 'ErrorBoundary');
    return appError.message;
  }
}

// Validation helpers
export class ValidationHelper {
  static validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  static validatePassword(password: string): boolean {
    return password.length >= 6;
  }

  static validateStockSymbol(symbol: string): boolean {
    // Basic validation for Taiwan stock symbols
    const symbolRegex = /^[0-9]{4}$/;
    return symbolRegex.test(symbol);
  }

  static validateQuantity(quantity: number): boolean {
    return Number.isInteger(quantity) && quantity > 0;
  }

  static validatePrice(price: number): boolean {
    return !isNaN(price) && price > 0;
  }

  static validateDisplayName(name: string): boolean {
    return name.trim().length >= 2 && name.trim().length <= 50;
  }
}

// Toast notification helper for errors
export class ErrorNotificationHelper {
  static getErrorSeverity(error: AppError): 'error' | 'warning' | 'info' {
    const category = ErrorHandler.categorizeError(error);
    
    switch (category.severity) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'warning';
    }
  }

  static shouldRetry(error: AppError): boolean {
    const category = ErrorHandler.categorizeError(error);
    return category.isRetryable;
  }
  
  /**
   * 檢查是否為可以忽略的低級別錯誤
   */
  static isIgnorableError(error: AppError): boolean {
    const category = ErrorHandler.categorizeError(error);
    return category.severity === 'low' && category.category === 'api';
  }
}

export default ErrorHandler;
